import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios, { AxiosError } from 'axios';
import { BatchExportDTO } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useToastContext } from '../../context/ToastContext';
import { useBatchExportWebSocket } from '../../context/BatchExportWebSocketContext';
import { formatTimestamp, formatTimestampTime } from '../../formatTimestamp';
import Icon from '@mdi/react';
import { mdiDownload, mdiRefresh, mdiClockOutline, mdiCheckCircle, mdiAlertCircle, mdiCancel } from '@mdi/js';
import './BatchExports.scss';

const BatchExports: React.FC = () => {
  const { accessToken } = useAuth();
  const { showSuccess, showError } = useToastContext();
  const [batches, setBatches] = useState<BatchExportDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>();
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Subscribe to global WebSocket updates
  const { subscribe, isConnected } = useBatchExportWebSocket();

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVICE_BASEPATH}/batch-exports/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          sortBy: 'dateCreated',
          direction: 'desc',
          pageNumber: 0,
          pageSize: 50
        }
      });
      
      setBatches(response.data.content);
    } catch (error) {
      const axiosError = error as AxiosError;
      setError(axiosError.message);
      console.error('Failed to fetch batch exports:', axiosError.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) {
      fetchBatches();
    }
  }, [accessToken, fetchBatches]);

  // Subscribe to WebSocket updates
  useEffect(() => {
    const unsubscribe = subscribe((batchUpdate) => {
      // Update local state with batch updates
      setBatches(prev => prev.map(batch => 
        batch.id === batchUpdate.batchId 
          ? { 
              ...batch, 
              status: batchUpdate.status,
              processedRecords: batchUpdate.progress?.processedRecords || batch.processedRecords,
              errorMessage: batchUpdate.errorMessage || batch.errorMessage
            }
          : batch
      ));

      // Refresh the batch list when a batch completes
      if (batchUpdate.type === 'BATCH_COMPLETED') {
        fetchBatches();
      }
    });

    return unsubscribe;
  }, [subscribe, fetchBatches]);

  // Timer effect to update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleDownload = async (batchId: number, batchNumber: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVICE_BASEPATH}/batch-exports/${batchId}/download`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_export_${batchNumber}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess(`Downloaded batch export ${batchNumber}`);
    } catch (error) {
      console.error('Failed to download batch export:', error);
      showError('Failed to download batch export. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return { icon: mdiCheckCircle, className: 'status-completed' };
      case 'processing':
        return { icon: mdiClockOutline, className: 'status-processing' };
      case 'failed':
        return { icon: mdiAlertCircle, className: 'status-failed' };
      case 'expired':
        return { icon: mdiCancel, className: 'status-expired' };
      default:
        return { icon: mdiClockOutline, className: 'status-pending' };
    }
  };

  const getTimeRemaining = useCallback((expirationDate?: Date) => {
    if (!expirationDate) return null;
    
    const expiry = new Date(expirationDate);
    const diff = expiry.getTime() - currentTime.getTime();
    
    if (diff <= 0) return { text: 'Expired', isExpired: true };
    
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;
    
    let timeText = '';
    if (days > 0) {
      timeText = `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      timeText = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      timeText = `${minutes}m ${seconds}s`;
    } else {
      timeText = `${seconds}s`;
    }
    
    return { text: timeText, isExpired: false };
  }, [currentTime]);

  const getProgressPercentage = (batch: BatchExportDTO) => {
    if (batch.status === 'COMPLETED') return 100;
    if (batch.status === 'FAILED' || batch.status === 'EXPIRED') return 0;
    if (batch.totalRecords === 0) return 0;
    
    return Math.round((batch.processedRecords / batch.totalRecords) * 100);
  };

  const getCompletionDuration = (dateCreated: Date, dateCompleted?: Date) => {
    if (!dateCompleted) return null;
    
    const created = new Date(dateCreated);
    const completed = new Date(dateCompleted);
    const diffMs = completed.getTime() - created.getTime();
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const isBatchExpired = useCallback((batch: BatchExportDTO) => {
    // First check the live timer calculation
    if (batch.expirationDate) {
      const expiry = new Date(batch.expirationDate);
      return currentTime > expiry;
    }
    
    // Fallback to backend-provided isExpired if available
    if (typeof batch.isExpired === 'boolean') {
      return batch.isExpired;
    }
    
    // If no expiration info, assume not expired
    return false;
  }, [currentTime]);

  if (loading) {
    return (
      <div className="batch-exports">
        <div className="batch-exports-container">
          <div className="batch-exports-loading">
            <div className="loading-spinner"></div>
            <p>Loading batch exports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="batch-exports">
        <div className="batch-exports-container">
          <div className="batch-exports-error">
            <Icon path={mdiAlertCircle} size={2} />
            <p>Error loading batch exports: {error}</p>
            <button onClick={fetchBatches} className="icon-button icon danger">
              <Icon path={mdiRefresh} size={1} />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="batch-exports">
      <div className="batch-exports-container">
        <div className="batch-exports-header">
          <h2>Batch Exports</h2>
          <button onClick={fetchBatches} className="icon-button icon">
            <Icon path={mdiRefresh} size={1} />
            Refresh
          </button>
        </div>

        {batches.length === 0 ? (
          <div className="no-batches">
            <p>No batch exports found. Create your first batch export by selecting account requests and choosing "Export Account Info".</p>
          </div>
        ) : (
          <div className="batch-list">
            {batches.map((batch) => {
              const statusInfo = getStatusIcon(batch.status);
              const timeRemainingInfo = getTimeRemaining(batch.expirationDate);
              const progress = getProgressPercentage(batch);
              const completionDuration = getCompletionDuration(batch.dateCreated, batch.dateCompleted);
              const isExpired = isBatchExpired(batch);
              
              // Debug logging (removed for production, but kept for testing)
              // console.log(`Batch ${batch.batchNumber}:`, {
              //   backendIsExpired: batch.isExpired,
              //   calculatedIsExpired: isExpired,
              //   timeRemaining: timeRemainingInfo,
              //   expirationDate: batch.expirationDate,
              //   hasResult: batch.hasResult,
              //   status: batch.status
              // });
              
              return (
                <div key={batch.id} className="batch-item">
                  <div className="batch-header">
                    <div className="batch-title">
                      <Icon path={statusInfo.icon} size={1} className={statusInfo.className} />
                      <span className="batch-number">{batch.batchNumber}</span>
                      <span className={`batch-status tag-${batch.status.toLowerCase()}`}>{batch.status}</span>
                    </div>
                    <div className="batch-actions">
                      {batch.status === 'COMPLETED' && batch.hasResult && (
                        <button 
                          {...(!isExpired && { onClick: () => handleDownload(batch.id, batch.batchNumber) })}
                          className={`icon-button icon success ${isExpired ? 'disabled' : ''}`}
                          title={isExpired ? "Download expired" : "Download CSV"}
                          disabled={isExpired}
                        >
                          <Icon path={mdiDownload} size={1} />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="batch-info">
                    <div className="batch-details">
                      <span><strong>Records:</strong> {batch.totalRecords}</span>
                      <span><strong>Created:</strong> {formatTimestampTime(batch.dateCreated.toString())}</span>
                      {completionDuration && (
                        <span><strong>Completed in:</strong> {completionDuration}</span>
                      )}
                      {timeRemainingInfo && batch.status === 'COMPLETED' && (
                        <span className={timeRemainingInfo.isExpired ? 'expired' : 'expires'}>
                          <strong>{timeRemainingInfo.isExpired ? 'Expired' : 'Expires in'}:</strong> {timeRemainingInfo.text}
                        </span>
                      )}
                    </div>
                    
                    {(batch.status === 'PROCESSING' || batch.status === 'PENDING') && (
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">
                          {batch.processedRecords} / {batch.totalRecords} records
                        </span>
                      </div>
                    )}
                    
                    {batch.errorMessage && (
                      <div className="error-message">
                        <Icon path={mdiAlertCircle} size={0.8} />
                        <span>{batch.errorMessage}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchExports;